/** Data health: feeds, session rules, coverage, dependencies, projection lag, queues, order rate (U056-U060). */
import {
  decodeDataHealthRow,
  decodeOrderRateRow,
  type DataHealthRow,
} from "../../contracts/ops.ts";
import { badge, instantCell, metric } from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import {
  formatBasisPoints,
  formatDurationUs,
  formatExchangeDate,
  formatQuantity,
} from "../../app/format.ts";
import { hashOf } from "../../app/router.ts";
import { renderStateBanner, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import {
  byKindOf,
  formatMs,
  healthTone,
  mountOps,
  panel,
  simpleTable,
  showEnvelope,
} from "../ops-common.ts";
import { orderRateContent } from "./order-rate.ts";

const orNone = (value: string | null): Child => metric(value);
const duration = (us: number | null): Child => metric(us === null ? null : formatDurationUs(us));

/** Draw the data-health panels from the rows read. */
export function drawData(rows: readonly DataHealthRow[]): readonly Child[] {
  const lags = byKindOf(rows, "projection_lag");
  return [
    h(
      "p",
      {},
      h("a", { attrs: { href: hashOf("data", "catalysts") } }, "News and catalysts"),
      " are listed under their own page.",
    ),
    panel(
      "Feeds and clock health",
      "data-feeds",
      simpleTable(
        "Feeds",
        [
          "Feed",
          "State",
          "Last event",
          "Age",
          "Sequence gaps",
          "Ingest latency",
          "Classification latency",
          "Clock offset",
          "Affected instruments",
        ],
        byKindOf(rows, "feed").map((row) => [
          row.feed,
          badge(row.state, healthTone(row.state)),
          instantCell(row.lastEventAt),
          duration(row.ageUs),
          String(row.sequenceGaps),
          duration(row.ingestLatencyUs),
          duration(row.classifyLatencyUs),
          orNone(row.clockOffsetUs === null ? null : formatMs(row.clockOffsetUs)),
          String(row.affectedInstruments),
        ]),
      ),
    ),
    panel(
      "Session, calendar and broker rules",
      "data-rules",
      simpleTable(
        "Effective rules",
        ["Rule", "Value", "Source", "Effective from", "Effective to", "Provenance"],
        byKindOf(rows, "rule").map((row) => [
          row.name,
          row.value,
          row.source,
          formatExchangeDate(row.effectiveFrom),
          orNone(row.effectiveTo === null ? null : formatExchangeDate(row.effectiveTo)),
          row.provenance,
        ]),
      ),
    ),
    panel(
      "Coverage and missingness",
      "data-coverage",
      simpleTable(
        "Coverage",
        [
          "Dataset",
          "Observed point-in-time",
          "Reconstructed",
          "Required features missing",
          "Lineage",
        ],
        byKindOf(rows, "coverage").map((row) => [
          row.dataset,
          formatBasisPoints(row.observedPitBp),
          formatBasisPoints(row.reconstructedBp),
          formatBasisPoints(row.missingRequiredBp),
          row.lineageRef,
        ]),
      ),
    ),
    panel(
      "Provider dependencies (metadata only, no secret values)",
      "data-dependencies",
      simpleTable(
        "Dependencies",
        [
          "Dependency",
          "State",
          "Owner",
          "Credential expires",
          "Quota",
          "Connections",
          "Licence",
          "Retention",
        ],
        byKindOf(rows, "dependency").map((row) => [
          row.name,
          badge(row.state, healthTone(row.state)),
          row.owner,
          orNone(
            row.credentialExpiresOn === null ? null : formatExchangeDate(row.credentialExpiresOn),
          ),
          orNone(
            row.quotaUsed === null || row.quotaLimit === null
              ? null
              : `${formatQuantity(row.quotaUsed)} of ${formatQuantity(row.quotaLimit)}`,
          ),
          String(row.connections),
          row.licence,
          row.retentionCovered ? "covered" : "not covered",
        ]),
      ),
    ),
    panel(
      "Projection lag",
      "data-lag",
      simpleTable(
        "Projection lag",
        ["Projection", "Epoch", "Applied lag", "Freshness bound", "Freshness"],
        lags.map((row) => [
          row.projection,
          String(row.epoch),
          duration(row.appliedLagUs),
          formatDurationUs(row.freshnessBoundUs),
          badge(row.freshness, row.freshness === "FRESH" ? "ok" : "warn"),
        ]),
      ),
    ),
    panel(
      "Capture, outbox and queues",
      "data-queues",
      simpleTable(
        "Queues",
        ["Queue", "Depth", "Oldest actionable event", "Failed messages", "Gaps", "Recovery"],
        byKindOf(rows, "queue").map((row) => [
          row.name,
          String(row.depth),
          duration(row.oldestActionableAgeUs),
          String(row.failedMessages),
          String(row.gaps),
          row.recoveryRef,
        ]),
      ),
    ),
  ];
}

/** The order-rate panel reads the telemetry projection on its own, so its state is its own. */
function orderRate(ctx: ViewContext, body: HTMLElement): void {
  const banner = h("div", { class: "banner-slot" });
  const freshness = h("div", { class: "freshness-slot" });
  const content = h("div", { attrs: { "data-order-rate": "" } });
  banner.append(renderStateBanner({ kind: "loading" }));
  body.append(
    h(
      "section",
      { class: "panel order-rate", attrs: { "aria-labelledby": "data-order-rate" } },
      h("h2", { attrs: { id: "data-order-rate" } }, "Order-rate token bucket"),
      banner,
      freshness,
      content,
    ),
  );
  void ctx.client
    .read("telemetry", decodeOrderRateRow)
    .then((answer) => {
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      showEnvelope({ banner, freshness }, answer, ctx.streamSequence.get());
      const [row] = answer.page.rows;
      setChildren(content, row === undefined ? [] : orderRateContent(row));
    })
    .catch((error: unknown) => {
      setChildren(banner, [renderStateBanner(stateOfError(error))]);
    });
}

/** Mount the data-health view. */
export function mountData(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "data",
    title: "Data health",
    projection: "data_health",
    row: decodeDataHealthRow,
    draw: (rows) => drawData(rows),
    extra: orderRate,
  });
}
